/**
 * Aviso con boton para publicar de una vez los productos ocultos que ya tienen
 * foto, precio y stock (ADM-03).
 *
 * Existe por lo que deja la primera sincronizacion real con Random: los
 * productos nuevos aterrizan ocultos a proposito (RND-01) y el importador de
 * fotos (ADM-12) los deja completos, pero son miles, y publicarlos con la
 * seleccion de la tabla —dieciseis por pagina— no es una opcion. La condicion
 * de "listo" la decide el servidor; aca solo se muestra la cifra y se confirma.
 *
 * Pide confirmacion en un modal, igual que la edicion masiva: toca miles de
 * productos de un clic, aunque se deshaga ocultandolos de nuevo. Reusa el
 * estilo de `.barra-edicion-masiva` porque es el mismo tipo de pieza (una
 * accion sobre muchos productos, sobre la tabla) y no amerita otro.
 *
 * No se dibuja cuando no hay nada listo: el aviso es para actuar, no para
 * informar que no hay nada que hacer.
 */
import { useState } from 'react';

import { usePeticion } from '../../hooks/use-peticion';
import { obtenerListosParaPublicar, publicarListos } from '../../servicios/administracion.servicio';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';

export interface BarraPublicarListosProps {
  /** Cambia cuando la tabla se refresca, para volver a contar. */
  version: number;
  /** Se dispara despues de publicar con exito, para refrescar la tabla. */
  onAplicado(): void;
}

export function BarraPublicarListos({ version, onAplicado }: BarraPublicarListosProps) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const listos = usePeticion((senal) => obtenerListosParaPublicar(senal), [version]);
  const cantidad = listos.datos?.cantidad ?? 0;

  if (cantidad === 0) return null;

  function cerrar(): void {
    if (enviando) return;
    setConfirmando(false);
    setFalla(null);
  }

  async function publicar(): Promise<void> {
    setEnviando(true);
    setFalla(null);
    try {
      await publicarListos();
      setConfirmando(false);
      onAplicado();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  const productos = cantidad === 1 ? 'producto oculto tiene' : 'productos ocultos tienen';

  return (
    <>
      <div className="barra-edicion-masiva" role="region" aria-label="Productos listos para publicar">
        <span className="n">
          {numero(cantidad)} {productos} foto, precio y stock, y se pueden publicar
        </span>
        <span className="acciones-fila">
          <button
            type="button"
            className="btn btn-sm btn-primario"
            onClick={() => {
              setConfirmando(true);
            }}
          >
            <Icono nombre="ojo" />
            Publicar los listos
          </button>
        </span>
      </div>

      <Modal
        titulo="Publicar los productos listos"
        bajada={`${numero(cantidad)} ${cantidad === 1 ? 'producto' : 'productos'}`}
        abierto={confirmando}
        onCerrar={cerrar}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <div className="formulario-admin">
          <p>
            Vas a publicar {numero(cantidad)} {cantidad === 1 ? 'producto' : 'productos'} que hoy
            {cantidad === 1 ? ' está oculto' : ' están ocultos'} y ya {cantidad === 1 ? 'tiene' : 'tienen'}{' '}
            foto, precio y stock. Van a aparecer en la tienda en su categoría actual —los que
            llegaron de Random siguen en «Sin clasificar» hasta que alguien los mueva—, y se
            pueden ocultar de nuevo uno por uno o en masa.
          </p>
          <div className="modal-acciones">
            <button type="button" className="btn btn-contorno" onClick={cerrar} disabled={enviando}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primario"
              disabled={enviando}
              onClick={() => {
                void publicar();
              }}
            >
              {enviando ? 'Publicando…' : 'Publicar'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
